import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getPaymentMethods, togglePaymentMethodStatus, deletePaymentMethod } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const PaymentMethods = () => {
    const navigate = useNavigate();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });

    useEffect(() => {
        fetchPaymentMethods();
    }, [filters]);

    // Inject custom styles
    useEffect(() => {
        const paymentMethodsStyles = `
            .table td {
                vertical-align: middle;
            }
            .btn-icon-xxs {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                transition: all 0.3s ease;
            }
            .btn-icon-xxs:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            }
            .table-hover tbody tr:hover {
                background-color: #f8f9fa;
                transition: background-color 0.2s ease;
            }
            .payment-qr-code {
                width: 50px;
                height: 50px;
                cursor: pointer;
                object-fit: contain;
                border-radius: 6px;
                border: 2px solid #e9ecef;
                padding: 2px;
                transition: all 0.3s ease;
            }
            .payment-qr-code:hover {
                border-color: #0d6efd;
                transform: scale(1.1);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            }
            .card {
                transition: all 0.3s ease;
            }
            .card:hover {
                box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
            }
            .avatar-sm {
                width: 2.5rem;
                height: 2.5rem;
            }
            .avatar-title {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        `;

        const styleId = 'payment-methods-custom-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = paymentMethodsStyles;
            document.head.appendChild(style);
        }
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, []);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            const response = await getPaymentMethods();
            if (response.success) {
                const methods = response.data || [];
                setPaymentMethods(methods);
                
                // Calculate summary
                const total = methods.length;
                const active = methods.filter(m => m.status === 'active').length;
                const inactive = total - active;
                setSummary({ total, active, inactive });
                
                setError(null);
            } else {
                throw new Error(response.message || 'Failed to fetch payment methods');
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setError('Failed to fetch payment methods');
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getFilteredPaymentMethods = () => {
        let filtered = [...paymentMethods];

        if (filters.status) {
            filtered = filtered.filter(method => method.status === filters.status);
        }

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(method =>
                (method.accountHolderName && method.accountHolderName.toLowerCase().includes(searchLower)) ||
                (method.bankName && method.bankName.toLowerCase().includes(searchLower)) ||
                (method.accountNumber && method.accountNumber.includes(searchLower)) ||
                (method.ifscCode && method.ifscCode.toLowerCase().includes(searchLower)) ||
                (method.upiId && method.upiId.toLowerCase().includes(searchLower))
            );
        }

        return filtered;
    };

    const handleToggleStatus = async (id, currentStatus) => {
        const confirmed = window.confirm(
            `Do you want to ${currentStatus === 'active' ? 'deactivate' : 'activate'} this payment method?`
        );

        if (!confirmed) return;

        try {
            await togglePaymentMethodStatus(id);
            alert(`Payment method has been ${currentStatus === 'active' ? 'deactivated' : 'activated'}!`);
            fetchPaymentMethods();
        } catch (error) {
            alert(error.message || "Failed to update payment method status");
        }
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm(
            "Are you sure? Once deleted, you will not be able to recover this payment method!"
        );

        if (!confirmed) return;

        try {
            await deletePaymentMethod(id);
            alert("Payment method has been deleted!");
            fetchPaymentMethods();
        } catch (error) {
            alert(error.message || "Failed to delete payment method");
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusBadge = (status) => {
        const variants = {
            active: 'success',
            inactive: 'warning',
            blocked: 'danger'
        };
        return <Badge bg={variants[status] || 'primary'}>{status}</Badge>;
    };

    const filteredMethods = getFilteredPaymentMethods();

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-xxl">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Payment Methods" sidebutton={false} />

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-xl-4 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h4 className="fs-18 font-w600 mb-1">{summary.total || 0}</h4>
                                <span className="text-muted">Total Payment Methods</span>
                            </div>
                            <div className="d-inline-block position-relative donut-chart-sale">
                                <i className="fa fa-credit-card fa-2x text-primary"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-xl-4 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h4 className="fs-18 font-w600 mb-1">{summary.active || 0}</h4>
                                <span className="text-muted">Active Methods</span>
                            </div>
                            <div className="d-inline-block position-relative donut-chart-sale">
                                <i className="fa fa-check-circle fa-2x text-success"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-xl-4 col-sm-6 mb-3">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h4 className="fs-18 font-w600 mb-1">{summary.inactive || 0}</h4>
                                <span className="text-muted">Inactive Methods</span>
                            </div>
                            <div className="d-inline-block position-relative donut-chart-sale">
                                <i className="fa fa-ban fa-2x text-warning"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom">
                            <Card.Title className="mb-0">Payment Methods</Card.Title>
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="info" 
                                    className="btn-rounded"
                                    onClick={() => navigate('/admin/payment-method-mapping')}
                                >
                                    <span className="btn-icon-start text-info">
                                        <i className="fa fa-link color-info" />
                                    </span>
                                    User Mappings
                                </Button>
                                <Button 
                                    variant="primary" 
                                    className="btn-rounded"
                                    onClick={() => navigate('/admin/add-payment-method')}
                                >
                                    <span className="btn-icon-start text-primary">
                                        <i className="fa fa-plus color-primary" />
                                    </span>
                                    Add New Payment Method
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Filters */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Search</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <i className="fa fa-search"></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, bank, account, IFSC, UPI..."
                                                name="search"
                                                value={filters.search}
                                                onChange={handleFilterChange}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="blocked">Blocked</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={5} className="d-flex align-items-end">
                                    {(filters.search || filters.status) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => setFilters({ status: '', search: '' })}
                                        >
                                            <i className="fa fa-times me-2"></i>
                                            Clear Filters
                                        </Button>
                                    )}
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <Table className="table table-responsive-md table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '80px' }}><strong>#</strong></th>
                                            <th><strong>ACCOUNT HOLDER</strong></th>
                                            <th><strong>BANK DETAILS</strong></th>
                                            <th><strong>UPI DETAILS</strong></th>
                                            <th><strong>STATUS</strong></th>
                                            <th><strong>CREATED AT</strong></th>
                                            <th><strong>ACTIONS</strong></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMethods.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="text-muted">
                                                        <i className="fa fa-inbox fa-3x mb-3 d-block"></i>
                                                        <p className="mb-0">No payment methods found</p>
                                                        {filters.search || filters.status ? (
                                                            <small>Try adjusting your filters</small>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredMethods.map((method, index) => (
                                                <tr key={method._id || method.id}>
                                                    <td><strong>{index + 1}</strong></td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="avatar-sm me-2">
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                                                                    <i className="fa fa-user"></i>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <strong>{method.accountHolderName || '-'}</strong>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong className="text-primary">{method.bankName || '-'}</strong>
                                                            <div className="text-muted mt-1">
                                                                <small><i className="fa fa-university me-1"></i>A/C: {method.accountNumber || '-'}</small>
                                                            </div>
                                                            <div className="text-muted">
                                                                <small><i className="fa fa-barcode me-1"></i>IFSC: {method.ifscCode || method.ifsc || '-'}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {(method.upiId || method.upiQrCode) ? (
                                                            <div>
                                                                {method.upiId && (
                                                                    <div className="mb-2">
                                                                        <Badge bg="info" className="me-1">
                                                                            <i className="fa fa-mobile me-1"></i>UPI ID
                                                                        </Badge>
                                                                        <span className="text-dark">{method.upiId}</span>
                                                                    </div>
                                                                )}
                                                                {method.upiQrCode && (
                                                                    <div>
                                                                        <img
                                                                            src={(() => {
                                                                                // Normalize path: replace backslashes with forward slashes
                                                                                let qrPath = method.upiQrCode.replace(/\\/g, '/');
                                                                                
                                                                                // If path already starts with http, use it as is
                                                                                if (qrPath.startsWith('http://') || qrPath.startsWith('https://')) {
                                                                                    return qrPath;
                                                                                }
                                                                                
                                                                                // Get base URL
                                                                                const baseUrl = process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                                                
                                                                                // Ensure path starts with /
                                                                                if (!qrPath.startsWith('/')) {
                                                                                    qrPath = `/${qrPath}`;
                                                                                }
                                                                                
                                                                                // Remove trailing slash from baseUrl if present
                                                                                const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                                                                                
                                                                                return `${cleanBaseUrl}${qrPath}`;
                                                                            })()}
                                                                            alt="UPI QR Code"
                                                                            className="payment-qr-code"
                                                                            onClick={() => {
                                                                                let qrPath = method.upiQrCode.replace(/\\/g, '/');
                                                                                
                                                                                if (qrPath.startsWith('http://') || qrPath.startsWith('https://')) {
                                                                                    window.open(qrPath, '_blank');
                                                                                    return;
                                                                                }
                                                                                
                                                                                const baseUrl = process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                                                
                                                                                if (!qrPath.startsWith('/')) {
                                                                                    qrPath = `/${qrPath}`;
                                                                                }
                                                                                
                                                                                const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                                                                                const fullUrl = `${cleanBaseUrl}${qrPath}`;
                                                                                window.open(fullUrl, '_blank');
                                                                            }}
                                                                            onError={(e) => {
                                                                                console.error('QR Code image failed to load:', method.upiQrCode);
                                                                                console.error('Attempted URL:', e.target.src);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">
                                                                <i className="fa fa-minus-circle me-1"></i>Not Available
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{getStatusBadge(method.status)}</td>
                                                    <td>
                                                        <div className="text-nowrap">
                                                            {formatDate(method.createdAt)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Button 
                                                                variant="primary" 
                                                                className="btn-icon-xxs"
                                                                title="Edit"
                                                                onClick={() => navigate(`/admin/edit-payment-method/${method._id || method.id}`)}
                                                            >
                                                                <i className="fa fa-edit"></i>
                                                            </Button>
                                                            <Button 
                                                                variant={method.status === 'active' ? 'warning' : 'success'} 
                                                                className="btn-icon-xxs"
                                                                title={method.status === 'active' ? 'Deactivate' : 'Activate'}
                                                                onClick={() => handleToggleStatus(method._id || method.id, method.status)}
                                                            >
                                                                <i className={`fa fa-${method.status === 'active' ? 'ban' : 'check'}`}></i>
                                                            </Button>
                                                            <Button 
                                                                variant="danger" 
                                                                className="btn-icon-xxs"
                                                                title="Delete"
                                                                onClick={() => handleDelete(method._id || method.id)}
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PaymentMethods;
